// src/shared/kontoSettings.js — „Настройки за Конто" на фирмата (Промени
// 16.10.2026, т. 3г, 3д, 3ж).
//
// Защо отделен прозорец: досега кодовете за Конто бяха разпилени — част в
// „Мои фирми → Склад и Конто", а валутата, курсът, кодовете на плащането,
// редът „РЕАЛИЗАЦИЯ" и мярката бяха заковани в програмата. Макс иска „всички
// колони да могат да се променят" и „при заплатите не е лошо как е
// направено". Затова: ЕДИН прозорец с всичко, което файлът „Импорт на
// документи" пише сам, на прост език, и отдолу — всички 78 колони с това
// какво влиза във всяка. Отваря се от „Фактури → Действия" и от „Мои фирми →
// Склад и Конто"; пише само company.konto (PUT /companies/:id/konto) — само
// кантората (клиентът не пипа Конто, т. 1).

import { el, field, input, row } from './dom.js';
import { toast } from './toast.js';
import { api } from './api.js';
import { openModal, closeModal } from './modal.js';
import { state } from './state.js';
import { UNITS } from './docLines.js';
import { categoryCombo } from './categoryCombo.js';
import catGroups from '../../services/categoryGroups.js';
import kontoCfg from '../../services/kontoConfig.js';
import kontoAn from '../../services/kontoAnalytics.js';
import fmt from '../../services/konto6Format.js';

const D = kontoCfg.DEFAULTS;
const DR = D.records || {};
const PRESETS = kontoCfg.RECORD_PRESETS || {};

// Какво пише програмата във всяка колона — за таблицата „Всички колони".
// Ключът е кодът на колоната; стойността — обяснение на прост език.
const SOURCE = {
  'H.DOC_TYPE': 'Вид документ — горе, „Документи"',
  'H.DOC_NO': '№ на фактурата',
  'H.DOC_DATE': 'датата на фактурата',
  'H.DOC_VALEUR': 'вальор — горе, „Валута и дати"',
  'H.TAX_EVENT_DATE': 'датата на фактурата',
  'H.FROM_DOC': 'при кредитно/дебитно известие — № на фактурата, към която е',
  'H.PARTNER_TYPE': 'вид контрагент — горе, „Контрагенти"',
  'H.PARTNER': 'кодът на клиента — горе, „Контрагенти"',
  'P.PARTNER_NAME': 'името на клиента',
  'P.PARTNER_ID': 'ЕИК на клиента',
  'P.PARTNER_VAT_ID': 'ДДС № на клиента (без него — ЕИК)',
  'H.PARTNER_ADDRESS': 'градът и адресът на клиента',
  'H.PARTNER_MOLL': 'МОЛ на клиента',
  'H.PAYMENT_TYPE': 'код на плащането — горе, „Плащане"',
  'H.DUE_DATE': 'падежът (без падеж — датата)',
  'H.VALUE_LOCAL': 'данъчната основа',
  'H.VAT_LOCAL': 'ДДС',
  'H.CURRENCY': 'код на валутата — горе',
  'H.EXCHANGE_RATE': 'курсът — горе',
  'H.VALUE_FOREIGN': 'данъчната основа',
  'H.VAT_FOREIGN': 'ДДС',
  'H.DESCRIPTION': 'името на реда — горе, „Редът на документа"',
  'D.ROW_NO': '№ на реда',
  'D.DEAL_TYPE': 'вид сделка по данъчния режим — горе',
  'D.ARTICLE_TYPE': 'тип артикул — горе (или на артикула)',
  'D.ARTICLE_ID': 'код на артикула — горе (или кодът за Конто на артикула)',
  'A.ARTICLE_NAME': 'името на реда — горе (или името на артикула)',
  'D.MEASURE': 'мярката — горе (или кодът на мярката на реда)',
  'D.QTY': '1 (или количеството на реда с артикул)',
  'D.PRICE_LOCAL': 'единичната цена', 'D.PRICE_FOREIGN': 'единичната цена',
  'D.PRICE_VAT_LOCAL': 'ДДС за единица', 'D.PRICE_VAT_FOREIGN': 'ДДС за единица',
  'D.VALUE_LOCAL': 'стойността на реда', 'D.VALUE_FOREIGN': 'стойността на реда',
  'D.VAT_LOCAL': 'ДДС на реда', 'D.VAT_FOREIGN': 'ДДС на реда',
  'D.DESCRIPTION': 'описанието на реда без артикул',
};

const DEAL_LABELS = [
  ['domestic', 'Вътрешна доставка'], ['eu_b2b', 'ВОД към фирма в ЕС'], ['eu_b2c_oss', 'Физ. лице в ЕС (OSS)'],
  ['eu_b2c_domestic', 'Физ. лице в ЕС — българско ДДС'], ['reverse_charge', 'Обратно начисляване'], ['exempt', 'Освободена доставка'],
];
const DOCS = [['invoice', 'Фактура'], ['credit', 'Кредитно известие'], ['debit', 'Дебитно известие']];
const PURCHASE_DOCS = [['purchase_invoice', 'Покупна фактура'], ['purchase_credit', 'Покупно кредитно известие'], ['purchase_debit', 'Покупно дебитно известие']];

const ph = (v) => (v === undefined || v === null || String(v) === '' ? '—' : String(v));
let helpSeq = 0;

// company: фирмата (с konto); onSaved(company) — по избор, след запис.
// opts.kind: 'sales' (по подразбиране) или 'purchases' — кой раздел е отворен.
// Двата раздела са отделни (Макс, 27.10.2026): доставките имат свои кодове
// за плащане, вид сделка, валута и реквизити; празно = като при продажбите.
export function kontoSettingsDialog(company, onSaved, opts) {
  const u = state().user;
  if (!u || u.role === 'client') { toast('Настройките за Конто са само за кантората.', true); return null; }
  if (!company || !company.id) { toast('Първо запазете фирмата.', true); return null; }
  const k = (company.konto && typeof company.konto === 'object') ? company.konto : {};
  // „p:раздел" — настройка на доставките (company.konto.purchases[раздел]).
  const kp0 = (k.purchases && typeof k.purchases === 'object') ? k.purchases : {};
  const cur = (sec, key) => { const s = sec.startsWith('p:') ? kp0[sec.slice(2)] : k[sec]; return (s && s[key] != null) ? String(s[key]) : ''; };
  // Сивото в празно поле на доставките — какво ще важи: стойността от продажбите.
  const S = kontoCfg.forCompany(company);
  // inputs[sec][key] = поле; събира се накрая в същата форма.
  const inputs = {};
  const inp = (sec, key, placeholder, max, extra) => {
    inputs[sec] = inputs[sec] || {};
    const i = input(Object.assign({ value: cur(sec, key), placeholder: ph(placeholder), maxlength: String(max || 20) }, extra || {}));
    inputs[sec][key] = i;
    return i;
  };
  const sel = (sec, key, options, def) => {
    inputs[sec] = inputs[sec] || {};
    const s = el('select', {}, ...options.map(([v, t]) => el('option', { value: v, text: t })));
    s.value = cur(sec, key) || (def == null ? '' : def);
    inputs[sec][key] = s;
    return s;
  };
  // Изборът на категория — същата търсачка като в доставката (ПРОМТ-46);
  // в inputs стои обвивка със .value, за да се събира като всяко поле.
  const catPick = (sec, key, value, cats, extra) => {
    inputs[sec] = inputs[sec] || {};
    const cb = categoryCombo(Object.assign({ categories: cats, value, inline: true }, extra || {}));
    inputs[sec][key] = { get value() { return String(cb.getValue() || ''); }, set value(v) { cb.setValue(v); }, combo: cb };
    return cb;
  };
  const note = (text) => el('p', { class: 'ks-note', text });
  // Правилото „настройки за начинаещ" (Макс, 29.10.2026; CLAUDE.md →
  // „Екраните с настройки"): под всяко поле — какво прави, откъде да се вземе
  // стойността в Конто и пример; празното поле значи подразбирането в сиво.
  // Екранният четец го чете заедно с полето (aria-describedby).
  const hf = (label, control, h, opts2) => {
    const f = field(label, control, opts2);
    if (!h) return f;
    const id = 'ks-help-' + (++helpSeq);
    f.appendChild(el('p', { class: 'ks-help', id, text: [h.what, h.where ? 'Откъде: ' + h.where : '', h.ex ? 'Пример: ' + h.ex : ''].filter(Boolean).join(' ') }));
    const t = control && control.matches && control.matches('input,select,textarea') ? control : (control && control.querySelector ? control.querySelector('input,select,textarea') : null);
    if (t) t.setAttribute('aria-describedby', id);
    return f;
  };
  // Техническите полета — в разгъваемо „За напреднали": в повечето фирми не
  // се пипат (подразбирането е от данните на Конто).
  const advanced = (...kids) => el('details', { class: 'ks-more ks-advanced' }, el('summary', { text: 'За напреднали (рядко се пипа)' }), ...kids);
  // Нивата на партидата (412, 4532) — по едно падащо меню за всяко ниво
  // (Макс, 27.10.2026: „при всяка фирма е различно"). Записва се като
  // „partner,no,date" — kontoConfig.lotLevels го чете.
  const LOT_NAMES = { partner: 'Клиент (шифърът)', no: '№ на фактурата', date: 'Дата на фактурата', deal: 'Вид сделка' };
  // Какво казва Конто за сметката (ОТГОВОР-ПРОМТ-24) — над менютата.
  const AN = kontoCfg.analyticsOf(company);
  const analyticsNote = (acc) => {
    const a = AN && AN[acc];
    if (!a) return null;
    if (!a.levels) return el('p', { class: 'ks-note sev-warn', text: 'В Конто ' + acc + ' = ' + a.text + '. Има ниво, което CRM-ът не попълва — тези записвания проверете в Конто на ръка.' });
    return note('В Конто ' + acc + ' = ' + a.text + ' (данните от Конто, 27.10.2026) — това е подредено по подразбиране.');
  };
  // sec: 'records' (продажби) или 'p:records' (доставки, ПРОМТ-29);
  // partnerName: как се казва нивото „контрагент" (клиент / доставчик).
  const lotPicker = (key, label, def, sec, partnerName) => {
    const section = sec || 'records';
    const parts = kontoCfg.LOT_PARTS[key];
    const names = Object.assign({}, LOT_NAMES, partnerName ? { partner: partnerName } : {});
    const now = kontoCfg.lotLevels(key, cur(section, key) || def) || kontoCfg.lotLevels(key, def) || [];
    const sels = parts.map((_, i) => {
      const s = el('select', {}, el('option', { value: '', text: '— няма —' }), ...parts.map((p) => el('option', { value: p, text: names[p] })));
      s.value = now[i] || '';
      return s;
    });
    inputs[section] = inputs[section] || {};
    // value като на обикновено поле: четене — нивата през запетая; запис
    // (бутонът „Попълни по данните от Конто") — подрежда менютата.
    inputs[section][key] = {
      get value() { return sels.map((s) => s.value).filter(Boolean).join(','); },
      set value(v) { const lv = kontoCfg.lotLevels(key, v) || []; sels.forEach((s, i) => { s.value = lv[i] || ''; }); },
    };
    return row(...sels.map((s, i) => field(label + ' — ниво ' + (i + 1), s, { size: 's' })));
  };

  // ---- Как се внасят продажбите: Excel (документи) или TXT (записвания) ----
  // Етап 3, по отговора на агента на Конто: в Маятико, Къщата ви и ТЕЛ ДЕМ
  // „Импорт на документи" изобщо не тръгва (няма видове документи в модул
  // „Бизнес"), а записванията работят без настройки в Конто — CRM-ът сам дава
  // сметките. Подразбирането остава Excel: фирма, която не е пипната, получава
  // същия файл като досега.
  const modeSel = sel('records', 'mode', [
    ['documents', '„Импорт на документи" — Excel, както досега'],
    ['records', '„Импорт на записвания" — TXT, CRM-ът дава сметките (препоръчва се)'],
  ], DR.mode || 'documents');
  const revRows = [];
  for (let i = 1; i <= kontoCfg.REVENUE_SLOTS; i++) {
    revRows.push(el('tr', {},
      el('th', { scope: 'row', class: 'num', text: String(i) }),
      el('td', {}, inp('records', 'rev' + i + '_name', i === 1 ? 'напр. Реализация' : '', 60, { 'aria-label': 'Вид приход ' + i + ' — име' })),
      el('td', {}, inp('records', 'rev' + i + '_account', i === 1 ? 'напр. 702' : '', 10, { inputmode: 'numeric', 'aria-label': 'Вид приход ' + i + ' — сметка' })),
      el('td', {}, inp('records', 'rev' + i + '_lot', i === 1 ? 'напр. 001/01' : '', 60, { 'aria-label': 'Вид приход ' + i + ' — партида (нивата през /)' }))));
  }
  const revTable = el('table', { class: 'ks-docs' },
    el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Име (вижда се на реда във фактурата)' }), el('th', { scope: 'col', text: 'Сметка' }), el('th', { scope: 'col', text: 'Партида' }))),
    el('tbody', {}, ...revRows));
  // Готовите настройки от отговора на агента на Конто — по ЕИК на фирмата.
  const preset = PRESETS[String(company.eik || '').replace(/\s+/g, '')];
  const presetBtn = preset ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Попълни по данните от Конто (' + preset.label + ')', onClick: () => {
    for (const key of kontoCfg.RECORD_KEYS) {
      const i = inputs.records && inputs.records[key];
      if (!i) continue;
      if (key in preset.records) i.value = preset.records[key];
      else if (/^rev\d_/.test(key)) i.value = '';
    }
    // Видът документ при плащане в брой (напр. 975 „Отчет за извършени
    // продажби") — по ОТГОВОР-ПРОМТ-43 той е различен само в 18 фирми.
    for (const [key, v] of Object.entries(preset.doc_codes_cash || {})) {
      const i = inputs.doc_codes_cash && inputs.doc_codes_cash[key];
      if (i) i.value = v;
    }
    showRecords();
    toast('Попълнено от данните на Конто — проверете и натиснете „Запази".');
  } }) : null;
  // Какво да се провери при тази фирма (BGL, подсистема, необичайни нива).
  const presetNote = preset && preset.note ? el('p', { class: 'sev-warn', text: 'Внимание за тази фирма: ' + preset.note }) : null;
  const kp = company.konto_partners;
  // ПРОМТ-28: шифрите идват само от Конто; CRM-ът не дава кодове и файлът е един.
  const knownNote = kp && kp.count
    ? note('Шифрите на клиентите са внесени от Конто' + (kp.imported_at ? ' на ' + new Date(kp.imported_at).toLocaleDateString('bg-BG') : '') + ' (' + kp.count + ' кода). Клиент с шифър отива във файла с кода си, без шифър — с ЕИК-а; Конто го намира по тях или го създава сам. Файлът е един.')
    : note('Шифрите на клиентите още не са внесени от Конто (Мои фирми → „Внос от Конто"). Дотогава клиентите отиват във файла с ЕИК-а си — Конто ги намира по него или ги създава сам с код по своята номерация.');
  const recBox = el('div', { class: 'ks-records' },
    presetBtn ? el('div', { style: 'margin:4px 0 8px' }, presetBtn, presetNote) : null,
    row(hf('Папка за продажбите в Конто', inp('records', 'folder', 'напр. 8', 10), { what: 'Номерът на папката в Конто, в която влизат продажбите.', where: 'в Конто — папката, в която досега записвате продажбите на ръка.', ex: '8.' }, { size: 's' }),
      hf('Текст на статията', inp('records', 'text', DR.text, 60), { what: 'Обяснителният текст на всяка статия в Конто.', ex: '„Реализация". Празно = „' + ph(DR.text) + '".' })),
    row(hf('Каса (в брой)', inp('records', 'acc_cash', DR.acc_cash, 10), { what: 'Сметката, по която отиват платените в брой.', ex: '501.' }, { size: 's' }),
      hf('Клиенти (по банка)', inp('records', 'acc_customer', DR.acc_customer, 10), { what: 'Сметката на вземанията от клиенти.', ex: '411.' }, { size: 's' }),
      hf('Аванси', inp('records', 'acc_advance', DR.acc_advance, 10), { what: 'Сметката на получените аванси.', ex: '412.' }, { size: 's' }),
      hf('ДДС продажби', inp('records', 'acc_vat', DR.acc_vat, 10), { what: 'Сметката на начисления ДДС.', ex: '4532.' }, { size: 's' })),
    advanced(
      note('Аналитичността (партидата) на 412 и 4532 е различна по фирма — подредена е по данните от Конто. Пипайте я само ако Конто откаже файла. Празно ниво — няма го.'),
      analyticsNote('412'),
      lotPicker('lot_412', '412 (аванси)', S.records.lot_412 || DR.lot_412 || 'doc_date_partner'),
      analyticsNote('4532'),
      lotPicker('lot_4532', '4532 (ДДС)', S.records.lot_4532 || DR.lot_4532 || 'deal_partner_doc'),
      row(hf('Вид сделка при 20 %', inp('records', 'deal_20', DR.deal_20, 10), { what: 'Кодът от номенклатура 51 за продажба с 20 % ДДС — от него Конто пълни дневника.', ex: '01.' }, { size: 's' }),
        hf('Вид сделка при 9 %', inp('records', 'deal_9', DR.deal_9, 10), { what: 'Същото за 9 % ДДС.', ex: '05.' }, { size: 's' }))),
    note('Видове приход: сметката и партидата (нивата през „/", напр. 001/01 — дейност / вид). Първият е по подразбиране. Ако са повече от един, във фактурата до всеки ред се избира кой е. Сметка извън група 70 (напр. 499 — средства на клиента) не е приход: не влиза в дневника по ДДС и редът трябва да е с 0 %.'),
    el('div', { class: 'ks-scroll' }, revTable),
    knownNote);
  function showRecords() { recBox.hidden = modeSel.value !== 'records'; }
  modeSel.addEventListener('change', showRecords);
  showRecords();

  // ---- Документи: код по банков път и (по избор) отделен код в брой ----
  const docRows = DOCS.map(([key, label]) => el('tr', {},
    el('th', { scope: 'row', text: label }),
    el('td', {}, inp('doc_codes', key, D.doc_codes[key], 20, { 'aria-label': label + ' — код (по банков път)' })),
    el('td', {}, inp('doc_codes_cash', key, 'като по банков път', 20, { 'aria-label': label + ' — код, когато е платена в брой' }))));
  const docTable = el('table', { class: 'ks-docs' },
    el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: 'Документ' }), el('th', { scope: 'col', text: 'По банков път' }), el('th', { scope: 'col', text: 'Платена в брой' }))),
    el('tbody', {}, ...docRows));

  // ---- Мерки: мярката в CRM → кодът ѝ в Конто ----
  const units = UNITS.slice();
  Object.keys(k.measure_codes || {}).forEach((m) => { if (!units.includes(m)) units.push(m); });
  const unitFields = units.map((m) => field(m, inp('measure_codes', m, (D.measure_codes || {})[m], 20), { size: 's' }));

  // ---- Допълнителни реквизити: 20 в заглавието, 20 на реда ----
  const fieldInputs = kontoCfg.FIELD_KEYS.map((key) => {
    const n = key.split('_').pop();
    const label = (key.startsWith('H.') ? 'Заглавие — реквизит ' : 'Ред — реквизит ') + n;
    return field(label, inp('fields', key, '', 100), { size: 'm' });
  });

  // ---- Всички колони на файла ----
  const allCols = el('table', { class: 'ks-cols' },
    el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Колона в Конто' }), el('th', { scope: 'col', text: 'Какво пише програмата' }))),
    el('tbody', {}, ...fmt.COLS.map((code, i) => el('tr', {},
      el('td', { class: 'num', text: String(i + 1) }),
      el('td', {}, el('div', { text: fmt.LABELS[i] }), el('div', { class: 'ks-code', text: code })),
      el('td', { text: SOURCE[code] || (/FIELD_/.test(code) ? 'празно — или постоянната стойност от „Допълнителни реквизити"' : '') })))));

  // ---- Доставките (отделен раздел) ----
  // ПРОМТ-29 (27.10.2026): доставките отиват в Конто САМО като „Импорт на
  // записвания" (TXT) — CRM-ът дава статията Дт разход/склад / Дт 4531 / Кт
  // 401 (services/kontoPurchaseRecords.js). Настройките на Excel-а (плащане,
  // валута, редът без артикул, реквизити, колоната за СС) вече не трябват.
  const same = (v) => 'като при продажбите: ' + ph(v);
  const PR = (kontoCfg.DEFAULTS.purchase_records || {});
  const P = kontoCfg.forCompany(company, 'purchases').purchase_records || PR;
  const PURCHASE_DEALS = [['domestic', 'Вътрешна доставка'], ['eu_b2b', 'ВОП от фирма в ЕС'], ['reverse_charge', 'Обратно начисляване'], ['exempt', 'Освободена доставка'], ['eu_b2c_oss', 'OSS'], ['eu_b2c_domestic', 'Физ. лице в ЕС']];
  // ---- Аналитичностите на гр. 30/60 (ПРОМТ-31) ----
  // Статиите на 601/602/609 са РАЗЛИЧНИ във всяка фирма, затова категорията
  // на разхода (обща) се връзва с код на статията ПО ФИРМА, по име; видът
  // дейност (ном. 20) е кодът с най-много статии; мерките — фиксираната
  // таблица, само ако кодът съществува. Данните идват от Конто
  // (GET /companies/:id/konto-analytics) — зареждат се след отварянето.
  const expBox = el('div', { class: 'ks-expense' },
    el('h4', { text: 'Статии на разходите, вид дейност и мерки' }),
    el('p', { class: 'muted', text: 'Зареждам данните от Конто…' }));
  const NEW_ITEM = 'new';
  async function loadExpense() {
    if (!company.id) return;
    let d;
    try { d = await api('/companies/' + company.id + '/konto-analytics'); } catch (e) { expBox.replaceChildren(el('h4', { text: 'Статии на разходите, вид дейност и мерки' }), el('p', { class: 'ks-note sev-warn', text: e.message })); return; }
    renderExpense(d);
  }
  // Преди ново рисуване (нова своя категория) — незаписаното в менютата
  // остава: стойностите отиват там, откъдето cur() чете.
  function keepUnsaved() {
    for (const sec of ['p:items', 'p:measures']) {
      const box = inputs[sec];
      if (!box) continue;
      const key = sec.slice(2);
      kp0[key] = Object.assign({}, kp0[key] || {});
      for (const k2 of Object.keys(box)) kp0[key][k2] = String(box[k2].value || '');
    }
    const rec = inputs['p:records'];
    for (const key of ['activity_code', 'project_code', 'mol_code']) {
      if (rec && rec[key]) kp0.records = Object.assign({}, kp0.records || {}, { [key]: String(rec[key].value || '') });
    }
    const ov = inputs['p:memory_override'];
    if (ov) kp0.memory_override = Object.fromEntries(Object.entries(ov).map(([e, x]) => [e, String(x.value || '')]));
  }
  function renderExpense(d) {
    // Менютата се строят наново — старите (махната категория) не се записват.
    inputs['p:items'] = {};
    inputs['p:measures'] = {};
    lastAccounts = d.imported_at ? (d.accounts || {}) : null;
    drawSample();
    expBox.replaceChildren(el('h4', { text: 'Статии на разходите, вид дейност и мерки' }));
    const cats = Array.isArray(d.categories) ? d.categories : [];
    const nsb = d.nsb || {};
    const df = d.defaults || null;
    if (!d.imported_at) {
      expBox.appendChild(el('p', { class: 'ks-note sev-warn', role: 'status', text: 'Няма данни от Конто за тази фирма (нивата на сметките и номенклатурите). Без тях файлът с доставките не се прави: Мои фирми → „Аналитичности и номенклатури".' }));
    } else {
      // ПРОМТ-45: вносът от JSON казва и датата на самия експорт от Конто.
      if (d.export_date) expBox.appendChild(note('Експортът от Конто е от ' + new Date(d.export_date).toLocaleDateString('bg-BG') + '.'));
      for (const w of Array.isArray(d.warnings) ? d.warnings : []) expBox.appendChild(el('p', { class: 'ks-note sev-warn', role: 'status', text: w.text || '' }));
      expBox.appendChild(note('Данните от Конто са от ' + new Date(d.imported_at).toLocaleDateString('bg-BG') + '. Кодовете на статиите в Конто са различни във всяка фирма — тук казвате коя статия на ТАЗИ фирма отговаря на всяка категория. „Нова статия" значи: следващият свободен код, Конто го създава при вноса (ще попита — отговорете „Да").'));
    }
    const itemsOf = (nom) => (nsb[String(nom)] && Array.isArray(nsb[String(nom)].items)) ? nsb[String(nom)].items : [];
    const optText = (i) => i.code + ' — ' + (i.name || '') + (i.count ? ' (' + i.count + ')' : '') + (i.source === 'crm' ? ' · дадена от CRM' : '');
    // Вид дейност (ном. 20) — само ако е ниво на разходна сметка във фирмата.
    const activityOn = !!(df && df.activity);
    const n20 = itemsOf('20').slice().sort((a, b) => (b.count || 0) - (a.count || 0) || String(a.code).localeCompare(String(b.code)));
    const curAct = cur('p:records', 'activity_code');
    const actList = n20.slice(0, 60);
    if (curAct && !actList.some((i) => i.code === curAct)) actList.unshift(n20.find((i) => i.code === curAct) || { code: curAct, name: '' });
    const actSel = el('select', {}, el('option', { value: '', text: '— изберете —' }), ...actList.map((i) => el('option', { value: i.code, text: optText(i) })));
    actSel.value = curAct;
    if (activityOn) {
      inputs['p:records'] = inputs['p:records'] || {};
      inputs['p:records'].activity_code = actSel;
      expBox.appendChild(row(field('Вид дейност (номенклатура 20)', actSel, { size: 'l' })));
      expBox.appendChild(note('Първото ниво на 601/602/609 в тази фирма. Предложен е кодът с най-много статии през 2026 г.; нищо не се създава в Конто.'));
    }
    // „Проект" (ном. 208 — ФОНДАЦИЯ ГИП) и „МОЛ / склад" (ном. 201 — 302/304 на
    // ПРЕМИЕР-АК, ФИНТ КОМЕРС): само ако сметката в Конто е разбита така (ПРОМТ-45).
    const extraSels = [];
    for (const [role, key, label, where, ex] of [['project', 'project_code', 'Проект', 'ниво на 601/602/609', '0008 ПРОЕКТ 6000.'], ['mol', 'mol_code', 'МОЛ / склад', 'ниво на 302/304', '001 Основен склад.']]) {
      const lv = df && df[role];
      if (!lv) continue;
      const list = itemsOf(lv.nom).filter((i) => !i.inactive && i.active !== false).sort((a, b) => (b.count || 0) - (a.count || 0) || String(a.code).localeCompare(String(b.code))).slice(0, 80);
      const now = cur('p:records', key);
      if (now && !list.some((i) => i.code === now)) list.unshift({ code: now, name: '(няма го в Конто)' });
      const s = el('select', {}, el('option', { value: '', text: '— изберете —' }), ...list.map((i) => el('option', { value: i.code, text: optText(i) })));
      s.value = now;
      inputs['p:records'] = inputs['p:records'] || {};
      inputs['p:records'][key] = s;
      extraSels.push({ s, code: df[key] || '' });
      expBox.appendChild(hf(label + ' (номенклатура ' + lv.nom + ')', s, {
        what: 'В Конто на тази фирма „' + (lv.nom_name || label) + '“ е ' + where + ' — всеки ред от доставките получава този код.',
        where: 'Конто → номенклатура ' + lv.nom + '. Предложен е кодът с най-много записвания; нищо не се създава.', ex,
      }, { size: 'l' }));
    }
    // Мерките (ном. $3) — фиксирана таблица; червено, ако кодът го няма във фирмата.
    const n3 = itemsOf('$3');
    const have3 = new Set(n3.map((i) => i.code));
    const mFields = kontoAn.MEASURES.map(([key, code, label]) => {
      const i = inp('p:measures', key, code, 10, { 'aria-label': 'Мярка ' + label + ' — код в Конто' });
      const hint = el('span', { class: 'ks-note', style: 'font-size:12px' });
      const check = () => { const v = i.value.trim(); hint.textContent = !v ? '' : (n3.length && !have3.has(v)) ? 'няма го в Конто' : (n3.find((x) => x.code === v) || {}).name || ''; hint.classList.toggle('sev-warn', !!v && n3.length > 0 && !have3.has(v)); };
      i.addEventListener('input', check); check();
      return field(label, el('div', {}, i, hint), { size: 's' });
    });
    expBox.appendChild(el('details', { class: 'ks-more' }, el('summary', { text: 'Мерки (номенклатура $3) — код в Конто' }),
      note('Второто ниво на 302/304: мярката на артикула → код в Конто. Таблицата е една за всички фирми (796 бр., 165 кг, 112 л…); кодът трябва да съществува в тази фирма.'),
      row(...mFields)));
    // Картата категория → статия.
    const tb = el('tbody');
    const sels = [];
    // По групи (ПРОМТ-46): Материали (601), Услуги (602), Други (609…) — със
    // заглавен ред; вътре редът е този от списъка с категориите.
    const gKey = (c) => c.group || catGroups.groupOf(c.account) || 'other';
    const gIdx = (c) => catGroups.GROUPS.findIndex((g) => g.key === gKey(c));
    const ordered = cats.map((c, i) => ({ c, i })).sort((x, y) => gIdx(x.c) - gIdx(y.c) || x.i - y.i).map((x) => x.c);
    let lastGroup = null;
    for (const c of ordered) {
      if (gKey(c) !== lastGroup) {
        lastGroup = gKey(c);
        tb.appendChild(el('tr', { class: 'ks-group-row' }, el('th', { scope: 'colgroup', colspan: '5', text: catGroups.groupTitle(lastGroup, cats.filter((x) => gKey(x) === lastGroup).map((x) => x.account)) })));
      }
      const lv = df && df.item_noms ? df.item_noms[c.account] : null;
      const tr = el('tr', {}, el('td', {}, el('span', { text: c.name }), c.own ? el('span', { class: 'tag ks-own', text: 'на фирмата' }) : null,
        c.own ? el('button', { class: 'btn sm ghost', type: 'button', text: 'Махни', 'aria-label': 'Махни категорията ' + c.name, onClick: () => archiveOwn(c) }) : null),
        el('td', { text: c.account }),
        // ПРОМТ-45: сметката по номенклатурата на НАП — само за сведение, не се пипа.
        el('td', { class: 'muted ks-nap', text: c.nap_account || '—', title: (d.nap_accounts || {})[c.nap_account] || '' }));
      if (!lv) {
        tr.appendChild(el('td', { class: 'muted', colspan: '2', text: d.imported_at ? 'сметка ' + c.account + ' е без статии в тази фирма' : '—' }));
        tb.appendChild(tr);
        continue;
      }
      const items = itemsOf(lv.nom);
      const s = el('select', { 'aria-label': 'Статия за „' + c.name + '"' },
        el('option', { value: '', text: '— не се ползва —' }),
        el('option', { value: NEW_ITEM, text: '— нова статия: следващият свободен код (Конто я създава) —' }),
        ...items.map((i) => el('option', { value: i.code, text: optText(i) })));
      const saved = cur('p:items', c.id);
      const sug = df && df.items ? df.items[c.id] : null;
      s.value = saved || '';
      if (saved && s.value !== saved) { s.appendChild(el('option', { value: saved, text: saved + ' — (няма го в Конто)' })); s.value = saved; }
      inputs['p:items'] = inputs['p:items'] || {};
      inputs['p:items'][c.id] = s;
      sels.push({ s, c, sug });
      tr.appendChild(el('td', {}, s));
      tr.appendChild(el('td', { class: 'muted', text: sug ? 'предложение: ' + sug.code + ' ' + sug.name + ({ exact: '', norm: ' (по името)', synonym: ' (по сходно име)' }[sug.how] ?? ' (по ключова дума)') : 'няма подобна статия — „нова статия"' }));
      tb.appendChild(tr);
    }
    const confirm = el('input', { type: 'checkbox', 'aria-label': 'Статиите на разходите са проверени и потвърдени' });
    const confirmedAt = cur('p:records', 'items_confirmed');
    confirm.checked = !!confirmedAt;
    inputs['p:records'] = inputs['p:records'] || {};
    inputs['p:records'].items_confirmed = { get value() { return confirm.checked ? (confirmedAt || new Date().toISOString()) : ''; }, set value(v) { confirm.checked = !!v; } };
    const fillBtn = el('button', { class: 'btn sm ghost', type: 'button', text: 'Попълни по данните от Конто', disabled: !df, onClick: () => {
      if (!df) return;
      if (activityOn && df.activity_code) actSel.value = df.activity_code;
      for (const x of extraSels) if (x.code) x.s.value = x.code;
      for (const [key] of kontoAn.MEASURES) { const i = inputs['p:measures'] && inputs['p:measures'][key]; if (i) { i.value = df.measures[key] || ''; i.dispatchEvent(new Event('input')); } }
      for (const x of sels) x.s.value = x.sug ? x.sug.code : NEW_ITEM;
      // Авансите (ПРОМТ-44): 402, когато Конто го е дал за фирмата, иначе 401.
      advSel.value = has402 ? '402' : '401';
      drawAdvSample();
      toast('Попълнено по данните от Конто — проверете статиите, отметнете „потвърдени" и натиснете „Запази".');
    } });
    expBox.appendChild(el('div', { class: 'ks-items' },
      el('h4', { text: 'Статии на разходите' }),
      note('За всяка категория — статията на тази фирма (второто ниво на 601/602/609). Сверено е ПО ИМЕ, не по код. Без потвърдена карта файлът с доставките не се прави.'),
      fillBtn,
      el('div', { class: 'ks-scroll' }, el('table', { class: 'ks-items-table' },
        el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: 'Категория' }), el('th', { scope: 'col', text: 'Сметка' }), el('th', { scope: 'col', text: 'Сметка НАП', title: 'Сметката от номенклатурата на НАП — само за сведение' }), el('th', { scope: 'col', text: 'Статия на фирмата' }), el('th', { scope: 'col', text: 'Предложение' }))), tb)),
      el('label', { class: 'choice' }, confirm, el('span', { text: 'Статиите на разходите са проверени и потвърдени' }))));
    // Асустрой (Макс, 30.10.2026): материалите не минават през склад — всички се трупат
    // в 601. Тогава ред без артикул, който OCR-ът не познае като друг разход, е „Материали“.
    expBox.appendChild(hf('Категория на редовете без артикул', catPick('p:records', 'default_category', cur('p:records', 'default_category'), cats.filter((c) => !c.archived_at || c.id === cur('p:records', 'default_category')),
      { placeholder: 'няма — програмата пита за всеки ред' }),
      { what: 'Когато OCR-ът прочете ред и не познае нито артикул, нито вид разход (наем, гориво…), редът получава тази категория — и сметката ѝ. Остава жълт за поглед.', where: 'как фирмата осчетоводява покупките в Конто: ако всички материали отиват направо в 601 без склад — изберете „Материали (601)“.', ex: '„Материали (601)“ за строителна фирма без склад; празно за търговец със склад.' }));
    if (d.deal_proposal) expBox.appendChild(dealProposalBox(d.deal_proposal));
    expBox.appendChild(overridesBox(cats));
    expBox.appendChild(ownCategoryForm(d));
    // Категориите — общи за всички фирми; пише ги само админът.
    if (u.role === 'admin') expBox.appendChild(categoriesEditor(cats));
  }

  // Видът сделка по ДДС от експорта на Конто (ПРОМТ-45): предложение по
  // колоните на дневника; „Попълни" слага кодовете в полетата, записът е с „Запази".
  function dealProposalBox(dp) {
    const box = el('div', { class: 'ks-deals' }, el('h4', { text: 'Вид сделка по данните от Конто' }));
    if (dp.none) { box.appendChild(note('В Конто на тази фирма няма видове сделка по ДДС — фирмата не е регистрирана по ЗДДС.')); return box; }
    const P2 = dp.purchases || {}, S2 = dp.sales || {};
    const show = (x) => (x ? x.code + ' — ' + x.name : '— няма —');
    const list = [['Покупка, пълен кредит', P2.deal_full], ['Покупка, частичен кредит', P2.deal_partial], ['Покупка без право на кредит', P2.deal_no_credit], ['Покупка без данък', P2.deal_no_vat],
      ['Продажба 20 %', S2.deal_20], ['Продажба 9 %', S2.deal_9], ['ВОП', S2.deal_vop], ['Чл. 82 (обратно начисляване)', S2.deal_82]];
    box.appendChild(note('Как е в Конто на тази фирма — по колоните на дневника за ДДС, не по името. Сверете, натиснете „Попълни видовете сделка“, после „Запази“.'));
    box.appendChild(el('ul', { class: 'ks-deal-list' }, ...list.map(([t, x]) => el('li', { text: t + ': ' + show(x) }))));
    box.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: 'Попълни видовете сделка', onClick: () => {
      const put = (sec, key, x) => { const i = inputs[sec] && inputs[sec][key]; if (i && x && x.code) i.value = x.code; };
      put('p:records', 'deal_full', P2.deal_full); put('p:records', 'deal_partial', P2.deal_partial);
      put('p:records', 'deal_no_credit', P2.deal_no_credit); put('p:records', 'deal_no_vat', P2.deal_no_vat);
      put('records', 'deal_20', S2.deal_20); put('records', 'deal_9', S2.deal_9);
      toast('Видовете сделка са попълнени по данните от Конто — натиснете „Запази“.');
    } }));
    return box;
  }

  // Изключения от общата памет по контрагент (ПРОМТ-45): същият доставчик
  // (ЕИК) получава в ТАЗИ фирма друга категория, отколкото в останалите.
  function overridesBox(cats) {
    const live = cats.filter((c) => !c.archived_at);
    inputs['p:memory_override'] = {};
    const tb = el('tbody');
    const addRow = (eik, cat) => {
      const s = catPick('p:memory_override', eik, cat || '', live, { ariaLabel: 'Категория за ЕИК ' + eik, placeholder: 'Пишете категорията…' });
      const tr = el('tr', {}, el('td', { text: eik }), el('td', {}, s),
        el('td', {}, el('button', { class: 'btn sm ghost', type: 'button', text: 'Махни', 'aria-label': 'Махни изключението за ЕИК ' + eik, onClick: () => { delete inputs['p:memory_override'][eik]; tr.remove(); } })));
      tb.appendChild(tr);
    };
    for (const [e, c] of Object.entries(kp0.memory_override || {})) addRow(e, c);
    const eikIn = input({ inputmode: 'numeric', maxlength: '15', placeholder: 'ЕИК на доставчика', 'aria-label': 'ЕИК на доставчика за изключение' });
    const addBtn = el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави изключение', onClick: () => {
      const e = eikIn.value.replace(/\D/g, '').replace(/^0+/, '');
      if (e.length < 8) { toast('Напишете ЕИК на доставчика (9 или 13 цифри).', true); return; }
      if (inputs['p:memory_override'][e]) { toast('За този ЕИК вече има изключение.', true); return; }
      addRow(e, '');
      eikIn.value = '';
    } });
    return el('div', { class: 'ks-overrides' }, hf('Изключения: доставчик → категория само за тази фирма', el('div', {},
      el('table', { class: 'ks-items-table' }, el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: 'ЕИК' }), el('th', { scope: 'col', text: 'Категория' }), el('th', { scope: 'col', text: 'Действие' }))), tb),
      row(eikIn, addBtn)), {
      what: 'Програмата помни за всеки доставчик (по ЕИК) коя категория му слагат ВСИЧКИ фирми и я предлага при нова доставка. Тук казвате изключението: за тази фирма този доставчик е друго.',
      where: 'ЕИК-ът на доставчика — от фактурата или от контакта.',
      ex: 'Лукойл (ЕИК 121699202) е „Гориво“ навсякъде, но за фирма-бензиностанция е друго — добавете го тук с нейната категория.',
    }));
  }

  // „+ Нова категория за тази фирма" (ПРОМТ-34): име, сметка от сметкоплана на
  // фирмата в Конто (гр. 20/30/60), статия — задължителна. Записва се веднага
  // (отделно от „Запази"), за да се появи в падащото меню на доставката.
  function ownCategoryForm(d) {
    const accs = Array.isArray(d.own_accounts) ? d.own_accounts : [];
    const nsb = d.nsb || {};
    const box = el('div', { class: 'ks-own-form', hidden: true });
    const openBtn = el('button', { class: 'btn sm ghost', type: 'button', text: '+ Нова категория за тази фирма', disabled: !d.imported_at,
      title: d.imported_at ? '' : 'Първо качете данните от Конто за фирмата', onClick: () => { box.hidden = false; openBtn.hidden = true; name.focus(); } });
    const name = input({ maxlength: '60', placeholder: 'напр. Фураж' });
    const acc = el('select', {}, el('option', { value: '', text: '— изберете сметка —' }),
      ...accs.map((a) => el('option', { value: a.account, disabled: !a.item_nom, text: a.account + (a.name ? ' — ' + a.name : '') + (a.item_nom ? '' : ' (без статии в Конто)') })));
    const item = el('select', { disabled: true }, el('option', { value: '', text: '— първо изберете сметка —' }));
    acc.addEventListener('change', () => {
      const a = accs.find((x) => x.account === acc.value);
      const list = a && a.item_nom && nsb[a.item_nom.nom] ? nsb[a.item_nom.nom].items || [] : [];
      item.replaceChildren(el('option', { value: '', text: '— изберете статия —' }),
        el('option', { value: NEW_ITEM, text: '— нова: следващият свободен код (Конто я създава) —' }),
        ...list.map((i) => el('option', { value: i.code, text: i.code + ' — ' + (i.name || '') })));
      item.disabled = !a || !a.item_nom;
    });
    const saveOwn = el('button', { class: 'btn sm', type: 'button', text: 'Добави категорията', onClick: async () => {
      if (!name.value.trim()) { toast('Напишете име на категорията.', true); return; }
      if (!acc.value) { toast('Изберете сметка.', true); return; }
      if (!item.value) { toast('Изберете статия на фирмата — без статия категорията не се записва.', true); return; }
      try {
        const r = await api('/purchases/own-categories', { method: 'POST', body: JSON.stringify({ company_id: company.id, name: name.value.trim(), account: acc.value, item: item.value }) });
        keepUnsaved();
        kp0.items = Object.assign({}, kp0.items || {}, { [r.category.id]: r.item });
        toast('Категорията „' + r.category.name + '" е добавена за тази фирма — вече е в падащото меню „Категория" на доставката.');
        loadExpense();
      } catch (e) { toast(e.message, true); }
    } });
    box.appendChild(note('Своя категория — само за тази фирма (напр. особен разход, който го няма в общия списък). Сметката е от сметкоплана на фирмата в Конто; статията е задължителна — съществуваща или нова (следващият свободен код).'));
    box.appendChild(row(field('Име на категорията', name, { size: 'm' }), field('Сметка', acc, { size: 'l' }), field('Статия на фирмата', item, { size: 'l' })));
    box.appendChild(el('div', { style: 'display:flex;gap:8px;margin-top:6px' }, saveOwn,
      el('button', { class: 'btn sm ghost', type: 'button', text: 'Отказ', onClick: () => { box.hidden = true; openBtn.hidden = false; } })));
    return el('div', { class: 'ks-own', style: 'margin:8px 0' }, openBtn, box);
  }
  async function archiveOwn(c) {
    try {
      const r = await api('/purchases/own-categories-archive', { method: 'POST', body: JSON.stringify({ company_id: company.id, id: c.id }) });
      keepUnsaved();
      if (r.removed && kp0.items) { kp0.items = Object.assign({}, kp0.items); delete kp0.items[c.id]; }
      toast(r.archived ? '„' + c.name + '" е ползвана в доставки — архивирана е: за нови редове вече не се предлага, старите доставки я пазят.' : '„' + c.name + '" е махната.');
      loadExpense();
    } catch (e) { toast(e.message, true); }
  }
  function categoriesEditor(cats) {
    const rowsEl = el('tbody');
    const mk = (c) => {
      const name = input({ value: c.name || '', maxlength: '60', 'aria-label': 'Име на категория' });
      const account = input({ value: c.account || '', maxlength: '10', inputmode: 'numeric', 'aria-label': 'Сметка на категория' });
      const kw = input({ value: (c.keywords || []).join(', '), maxlength: '200', 'aria-label': 'Ключови думи на категория' });
      // По количество (горивото): редът отива в Конто с количеството и цената.
      const qty = input({ type: 'checkbox', 'aria-label': 'По количество — ' + (c.name || 'категория') });
      qty.checked = c.qty === true;
      const tr = el('tr', {}, el('td', {}, name), el('td', {}, account), el('td', {}, kw), el('td', { style: 'text-align:center' }, qty),
        el('td', {}, el('button', { class: 'btn sm ghost', type: 'button', text: '×', 'aria-label': 'Махни категорията ' + (c.name || ''), onClick: () => tr.remove() })));
      tr._get = () => ({ id: c.id || '', name: name.value.trim(), account: account.value.trim(), keywords: kw.value.split(',').map((x) => x.trim()).filter(Boolean), qty: qty.checked });
      return tr;
    };
    cats.forEach((c) => rowsEl.appendChild(mk(c)));
    const saveBtn = el('button', { class: 'btn sm', type: 'button', text: 'Запази категориите', onClick: async () => {
      const list = [...rowsEl.children].map((tr) => tr._get());
      try {
        await api('/companies/konto-categories', { method: 'PUT', body: JSON.stringify({ categories: list }) });
        toast('Категориите са записани — важат за всички фирми.');
        loadExpense();
      } catch (e) { toast(e.message, true); }
    } });
    return el('details', { class: 'ks-more' }, el('summary', { text: 'Категории на разходите (общи за всички фирми)' }),
      note('Категорията е обща: име и сметка по подразбиране (602 услуги, 601 материали, 609 други, 621 лихви, 629 банкови такси). Ключовите думи помагат да се предложи статия по име. „По количество" — редът отива в Конто с количеството и цената (горивото). Статията във всяка фирма се избира горе.'),
      el('div', { class: 'ks-scroll' }, el('table', {}, el('thead', {}, el('tr', {}, el('th', { scope: 'col', text: 'Категория' }), el('th', { scope: 'col', text: 'Сметка' }), el('th', { scope: 'col', text: 'Ключови думи' }), el('th', { scope: 'col', text: 'По количество' }), el('th', { scope: 'col', text: '' }))), rowsEl)),
      el('div', { style: 'margin-top:6px;display:flex;gap:8px' },
        el('button', { class: 'btn sm ghost', type: 'button', text: 'Добави категория', onClick: () => rowsEl.appendChild(mk({ name: '', account: '602', keywords: [] })) }),
        saveBtn));
  }
  loadExpense();

  // ---- Аванси към доставчици (ПРОМТ-44, Макс 30.10.2026: „нека има избор") ----
  // services/kontoPurchaseRecords.js: 402 (подразбиране), 401 без 402 или на
  // ръка. Пробата отдолу показва статията по избрания начин с едни и същи числа.
  const ADV_OPTS = [
    ['402', 'По сметка 402 (препоръчва се)'],
    ['401', 'По 401 с минус (без 402)'],
    ['none', 'Не се изнася — водя го на ръка в Конто'],
  ];
  const advSel = sel('p:records', 'advance_mode', ADV_OPTS, '402');
  const a402 = AN && AN['402'];
  const has402 = !!(a402 && Array.isArray(a402.levels) && a402.levels.length && !/(^|\/)\s*44\s/.test(String(a402.text || '')));
  const advSample = el('div', { class: 'ks-sample', 'aria-live': 'polite' });
  function drawAdvSample() {
    const m = advSel.value || '402';
    const lot402 = a402 ? ' [' + a402.text + ']' : '';
    const lines = m === 'none'
      ? ['Авансовата фактура не влиза във файла (в прегледа е „пропусната"), а окончателна фактура с приспаднат аванс спира файла — двете ги записвате в Конто на ръка.']
      : m === '401'
        ? ['Авансова фактура 1 000 + 200 ДДС: Дт 4531 200 / Кт 401 200 с № и дата на авансовата (платените 1 200 вече са по Дт 401 от банката).',
          'Окончателна 3 000 с приспаднат аванс 1 000: Дт 60x 3 000 / Дт 4531 400 / Кт 401 1 000 с № и дата на АВАНСОВАТА (закрива я) / Кт 401 2 400 с № на окончателната.']
        : ['Авансова фактура 1 000 + 200 ДДС: Дт 402' + lot402 + ' 1 000 / Дт 4531 200 / Кт 401 1 200.',
          'Окончателна 3 000 с приспаднат аванс 1 000: Дт 60x 3 000 / Дт 4531 400 / Кт 402 1 000 с № и дата на АВАНСОВАТА / Кт 401 2 400.'];
    advSample.replaceChildren(...[
      el('div', { style: 'font-weight:600', text: 'Проба: как ще излезе авансът в Конто' }),
      m === '402' && !has402 ? el('p', { class: 'ks-note sev-warn', text: a402 && a402.text
        ? 'В Конто 402 на тази фирма е „' + a402.text + '" — CRM-ът не може да я попълни. Изберете „По 401 с минус" или „Не се изнася".'
        : 'Конто не е дало нивата на 402 за тази фирма — „Внос от Конто" → аналитичности. Дотогава файл с аванси по 402 не се прави.' }) : null,
      el('ul', { style: 'margin:4px 0;padding-left:20px' }, ...lines.map((t) => el('li', { text: t }))),
      note('В дневника за покупки окончателната влиза само с остатъка (2 000 основа, 400 ДДС).'),
    ].filter(Boolean));
  }
  advSel.addEventListener('change', drawAdvSample);
  drawAdvSample();
  const advBox = el('div', { class: 'ks-advance' },
    el('h4', { text: 'Аванси към доставчици' }),
    hf('Аванси към доставчици', advSel, {
      what: 'Как влиза в Конто платеният аванс (доставка с отметка „Авансова фактура") и как се закрива, когато дойде окончателната фактура с „Приспадни аванс".',
      where: 'вижте в Конто как е осчетоводен последният аванс на фирмата: има ли ред по 402. Ако има — „По сметка 402"; ако авансът стои по 401 на доставчика — „По 401 с минус".',
      ex: 'аванс 1 000 + 200 ДДС, окончателна 3 000: по 402 → Кт 402 1 000, 4531 400, Кт 401 2 400; по 401 → Кт 401 1 000 по партидата на аванса и Кт 401 2 400. Празно = „По сметка 402".',
    }),
    advSample);

  const purchBox = el('div', { class: 'ks-tab', 'data-tab': 'purchases' },
    note('Доставките отиват в Конто като „Импорт на записвания" (TXT): CRM-ът дава статията Дт разходна/складова сметка / Дт 4531 / Кт 401. Празно поле = подразбирането (стойността в сиво).'),
    el('h4', { text: 'Записвания' }),
    row(hf('Папка за доставките в Конто', inp('p:records', 'folder', P.folder || 'напр. 1', 10), { what: 'Номерът на папката в Конто, в която влизат покупките.', where: 'в Конто — папката, в която досега записвате доставките на ръка (за повечето фирми е попълнена от данните на Конто).', ex: '1.' }, { size: 's' }),
      hf('Текст на статията (доставки)', inp('p:records', 'text', 'сам: по категорията, напр. „Гориво — Петрол АД"', 60), { what: 'Празно = CRM-ът го пише сам: категорията на реда („Наем", „Гориво"), без категория — сметката („Стоки", „Материали", „ДМА"), при няколко вида — най-голямата част и „и др.", накрая доставчикът. Пишете тук само ако искате един и същ текст за всички доставки на фирмата.', ex: 'празно (препоръчва се) → „Гориво — Петрол АД".' })),
    note('Стока и материал отиват в складовата сметка на артикула (304/302, с код на артикула и количество). Услуга и ред без артикул — в „Сметка за разход" на реда, а без нея — в разхода по подразбиране.'),
    expBox,
    advBox,
    el('h4', { text: 'Доставчици' }),
    hf('Кодът на доставчика във файла', sel('p:values', 'partner_code', [
      ['', 'Като при продажбите'],
      ['acc_or_eik', 'Шифърът като доставчик, а без него — ЕИК-ът'],
      ['acc_only', 'Само шифърът — без шифър файлът не се прави'],
    ], ''), { what: 'По какво Конто ще познае доставчика.', ex: '„Шифърът като доставчик, а без него — ЕИК-ът".' }),
    advanced(
      el('h4', { text: 'Документи' }),
      row(...PURCHASE_DOCS.map(([key, label]) => field(label, inp('doc_codes', key, D.doc_codes[key], 20), { size: 's' }))),
      note('Кодът на вида документ в Конто. Празно = 942 / 973 / 972 — така са във всички фирми в Конто (данните от Конто, 29.10.2026).'),
      el('h4', { text: 'Сметки' }),
      row(hf('Доставчици (401)', inp('p:records', 'acc_supplier', PR.acc_supplier, 10), { what: 'Сметката на задълженията към доставчици.', ex: '401.' }, { size: 's' }),
        hf('ДДС покупки (4531)', inp('p:records', 'acc_vat', PR.acc_vat, 10), { what: 'Сметката на ДДС-а с право на кредит.', ex: '4531.' }, { size: 's' }),
        hf('Разход по подразбиране', inp('p:records', 'acc_expense', PR.acc_expense, 10), { what: 'Къде отива ред без артикул, без сметка и без категория.', ex: '602.' }, { size: 's' })),
      analyticsNote('401'),
      lotPicker('lot_401', '401 (доставчици)', P.lot_401 || 'partner,no,date', 'p:records', 'Доставчик (шифърът)'),
      analyticsNote('4531'),
      lotPicker('lot_4531', '4531 (ДДС покупки)', P.lot_4531 || 'deal,partner,no', 'p:records', 'Доставчик (шифърът)'),
      el('h4', { text: 'Вид сделка при покупките (ном. 51, първото ниво на 4531)' }),
      note('Кодовете са еднакви във всички фирми в Конто (данните от Конто, 29.10.2026): 04 пълен кредит, 02 без право, 06 частичен, 01 сделка без данък. Празно поле = този код. Без право и без данък — ред 4531 с ДДС 0 и цялата сума за колона 09 на дневника.'),
      row(field('Пълен кредит', inp('p:records', 'deal_full', P.deal_full || '04', 20), { size: 's' }),
        field('Без право на кредит', inp('p:records', 'deal_no_credit', P.deal_no_credit || '02', 20), { size: 's' }),
        field('Частичен кредит', inp('p:records', 'deal_partial', P.deal_partial || '06', 20), { size: 's' }),
        field('Сделка без данък', inp('p:records', 'deal_no_vat', P.deal_no_vat || '01', 20), { size: 's' })),
      el('details', { class: 'ks-more' }, el('summary', { text: 'По данъчен режим (рядко нужно)' }),
        row(...PURCHASE_DEALS.map(([key, label]) => field(label + ' (покупка)', inp('p:deal_types', key, key === 'domestic' ? 'като „Пълен кредит"' : same(S.deal_types[key]), 20), { size: 's' })))),
      row(hf('Вид контрагент — доставчик', inp('partner_types', 'supplier', D.partner_types.supplier, 20), { what: 'Номенклатурата на доставчиците в Конто — по нея „Внос от Конто" познава „Шифър като доставчик".', ex: '80.' }, { size: 's' }))));

  // ---- Изписването от склада (ПРОМТ-33) ----
  // Себестойността на продаденото → „Импорт на записвания" (services/kontoCogs.js):
  // Дт сметка за себестойност / Кт склад, с партидите по нивата на фирмата в
  // Конто. „Изнасяй и себестойността" и групирането дойдоха тук от екрана на
  // фирмата — празно в company.konto.cogs значи старото поле на фирмата.
  const CG = (k.cogs && typeof k.cogs === 'object') ? k.cogs : {};
  const BT = D.accounts_by_type || {};
  const cogsOn = el('input', { type: 'checkbox', 'aria-label': 'Изнасяй и себестойността' });
  cogsOn.checked = CG.export === 'yes' ? true : CG.export === 'no' ? false : company.konto_export_cogs !== false;
  inputs.cogs = inputs.cogs || {};
  inputs.cogs.export = { get value() { return cogsOn.checked ? 'yes' : 'no'; }, set value(v) { cogsOn.checked = v !== 'no'; } };
  const groupingSel = sel('cogs', 'grouping', [
    ['', 'Според склада (ресторант — по месец, търговия — по документ)'],
    ['by_document', 'По документ — по една статия на фактура'],
    ['by_day', 'По ден — една статия на ден'],
    ['by_month', 'По месец — една статия за месеца'],
  ], '');
  if (!CG.grouping && company.konto_cogs_grouping) groupingSel.value = company.konto_cogs_grouping;
  const cogsFolder = inp('cogs', 'folder', '1', 20);
  const cogsDoc = inp('cogs', 'doc_type', '979', 20);
  const accGoods = inp('cogs', 'acc_goods', (BT.goods || {}).cogs_account || '702', 10, { inputmode: 'numeric' });
  const accMat = inp('cogs', 'acc_material', (BT.material || {}).cogs_account || '601', 10, { inputmode: 'numeric' });
  // „Проба": как ще изглежда статията в Конто на тази фирма — нивата на
  // сметките от данните на Конто и примерна партида.
  const cogsSample = el('div', { class: 'ks-sample', 'aria-live': 'polite' });
  let lastAccounts = null;
  const EX_LOT = { article: '001', measure: '165', activity: '001', item: '01' };
  const sampleOf = (acc) => {
    const a = lastAccounts && lastAccounts[acc];
    if (!a) return acc + (lastAccounts ? ' (я няма в данните от Конто на фирмата — изписване по нея би спряло файла)' : '');
    const lv = Array.isArray(a.levels) ? a.levels : [];
    return acc + (lv.length ? ' [' + lv.map((l) => EX_LOT[l.role] || '?').join('][') + ']' : '') + ' — ' + (a.text || (lv.length ? '' : 'без аналитичност'));
  };
  function drawSample() {
    const rows2 = [
      ['Стока (1 кг)', accGoods.value.trim() || (BT.goods || {}).cogs_account || '702', (BT.goods || {}).stock_account || '304'],
      ['Материал (1 кг)', accMat.value.trim() || (BT.material || {}).cogs_account || '601', (BT.material || {}).stock_account || '302'],
    ];
    // replaceChildren (за разлика от el) пише null като текст — затова filter.
    cogsSample.replaceChildren(...[
      el('div', { style: 'font-weight:600', text: 'Проба: как ще излезе статията в Конто' }),
      lastAccounts ? null : el('p', { class: 'ks-note sev-warn', text: 'Няма данни от Конто за тази фирма — без тях файлът с изписванията не се прави (Мои фирми → „Аналитичности и номенклатури").' }),
      el('ul', { style: 'margin:4px 0;padding-left:20px' }, ...rows2.map(([lbl, dt, kt]) => el('li', { text: lbl + ': Дт ' + sampleOf(dt) + ' / Кт ' + sampleOf(kt) }))),
      note('В квадратните скоби е примерна партида: 001 — кодът на артикула в Конто, 165 — мярката „кг". Истинските кодове вижте в прегледа преди файла (Склад → Експорти за Конто → „Изписвания за Конто").'),
    ].filter(Boolean));
  }
  [accGoods, accMat].forEach((i) => i.addEventListener('input', drawSample));
  const itemGoods = inp('cogs', 'item_goods', '', 20);
  const itemMat = inp('cogs', 'item_material', '', 20);
  const cogsFill = el('button', { class: 'btn sm ghost', type: 'button', text: 'Попълни по данните от Конто', onClick: () => {
    // В данните от Конто няма ръчните изписвания по фирми — затова папката и
    // видът документ са подразбирането (1 / 979); човекът ги сверява в Конто.
    if (!cogsFolder.value.trim()) cogsFolder.value = '1';
    if (!cogsDoc.value.trim()) cogsDoc.value = '979';
    toast('Попълнено: папка 1, вид документ 979 — в данните от Конто няма ръчни изписвания, по които да се познае. Сверете ги с Конто и натиснете „Запази".');
  } });
  const cogsBox = el('div', { class: 'ks-tab', 'data-tab': 'cogs' },
    note('Изписването е себестойността на продаденото: всеки месец CRM-ът прави статия Дт сметка за себестойност (стоки 702, материали 601) / Кт склад (304 / 302), с количеството и стойността от склада. Файлът се сваля от Склад → „Експорти за Конто" → „Изписвания за Конто". Празно поле = подразбирането (в сиво).'),
    el('label', { class: 'choice' }, cogsOn, el('span', { text: 'Изнасяй и себестойността' })),
    el('p', { class: 'ks-help', text: 'Отметнато — в Склад има бутон „Изписвания за Конто". Махнете отметката, ако изписването в тази фирма се прави в Конто на ръка.' }),
    hf('Изписванията във файла за Конто', groupingSel, { what: 'Колко статии да има: по една на фактура, на ден или една за месеца.', ex: 'търговия — „По документ", ресторант — „По месец".' }),
    el('div', { style: 'margin:6px 0' }, cogsFill),
    row(hf('Папка за изписванията в Конто', cogsFolder, { what: 'Номерът на папката в Конто, в която влизат изписванията.', where: 'в Конто — папката, в която досега записвате изписването на ръка.', ex: '1.' }, { size: 's' }),
      hf('Вид документ', cogsDoc, { what: 'Кодът на вида документ на статията.', where: 'Конто → Номенклатури → Видове документи (напр. „Протокол за изписване").', ex: '979.' }, { size: 's' }),
      hf('Текст на статията (изписване)', inp('cogs', 'text', 'Изписване', 60), { what: 'Началото на текста — CRM-ът добавя документа и артикула.', ex: '„Изписване фактура № 101 — Кафе".' })),
    el('h4', { text: 'Сметка за себестойност по вид' }),
    note('Важи, когато артикулът и групата му нямат своя „Сметка себестойност" (Артикули → Групи). Продукцията (303) в CRM-а още няма свой вид артикул — сметките ѝ се задават в групата.'),
    row(hf('Стока', accGoods, { what: 'Сметката за себестойност на продадените стоки.', ex: '702.' }, { size: 's' }),
      hf('Материал', accMat, { what: 'Сметката за изразходваните материали (и ресторантът по касата).', ex: '601.' }, { size: 's' })),
    cogsSample,
    advanced(
      note('Само ако в Конто на тази фирма сметката за себестойност е по статии (напр. 601 = [вид дейност][статия]) — кодът на статията, по която да отива. Видът дейност и мерките са общи с доставките („Доставки" → „Статии на разходите, вид дейност и мерки").'),
      row(hf('Статия на себестойността — стоки', itemGoods, { what: 'Кодът на статията в Конто.', ex: '01.' }, { size: 's' }),
        hf('Статия на себестойността — материали', itemMat, { what: 'Кодът на статията в Конто.', ex: '01.' }, { size: 's' }))));
  drawSample();

  const salesBox = el('div', { class: 'ks-tab', 'data-tab': 'sales' },
    note('Тук е всичко, което програмата пише във файловете за Конто 6. Попълвате само каквото в Конто на тази фирма е различно. Празно поле = стойността в сиво.'),

    el('h4', { text: 'Как се внасят продажбите в Конто' }),
    hf('Файлът с продажбите', modeSel, { what: 'Какъв файл прави „Експорт за Конто 6" от Фактури.', where: 'ако не знаете — „Импорт на записвания" (TXT): работи без настройки в Конто.' }),
    note('„Импорт на записвания": CRM-ът дава готовата статия — в брой Дт 501, по банка Дт 411, аванс Кт 412, приходите по видове, ДДС с вида сделка за дневника. В Конто: Администриране → Връзка с други системи → Импорт на записвания. Не иска настройки в Конто.'),
    recBox,

    el('h4', { text: 'Документи' }),
    note('Кодът на вида документ в Конто (откъде: Конто → Номенклатури → Видове документи). Ако в Конто имате отделен вид документ за продажбите в брой (например свързан с каса 501), напишете го в „Платена в брой" — фактурите с начин на плащане „В брой" ще излизат с него.'),
    el('div', { class: 'ks-scroll' }, docTable),

    el('h4', { text: 'Клиенти' }),
    hf('Кодът на клиента във файла', sel('values', 'partner_code', [
      ['acc_or_eik', 'Счетоводният шифър, а без шифър — ЕИК-ът (както досега)'],
      ['acc_only', 'Само счетоводният шифър — без шифър файлът не се прави'],
    ], D.partner_code || 'acc_or_eik'), { what: 'По какво Конто ще познае клиента.', ex: 'първият избор — по шифъра, а без шифър по ЕИК-а; непознатия Конто създава сам.' }),
    note('Шифрите идват от Конто („Внос от Конто" в Мои фирми). Клиент без шифър отива във файла с ЕИК-а си и Конто го намира по него; непознат клиент Конто създава сам с код по своята номерация — той влиза в CRM-а при следващия внос. Прегледът преди файла казва кой е без шифър, само за сведение.'),

    advanced(
      el('h4', { text: 'Плащане' }),
      row(hf('Код „В брой"', inp('pay_codes', 'cash', D.pay_cash, 20), { what: 'Кодът на плащането „в брой" в Конто (само за Excel файла).', ex: ph(D.pay_cash) + '.' }, { size: 's' }),
        hf('Код „По банков път"', inp('pay_codes', 'bank', D.pay_bank, 20), { what: 'Кодът на плащането по банка.', ex: ph(D.pay_bank) + '.' }, { size: 's' })),

      el('h4', { text: 'Контрагенти' }),
      row(hf('Вид контрагент — клиент', inp('partner_types', 'customer', D.partner_types.customer, 20), { what: 'Номенклатурата на клиентите в Конто.', ex: ph(D.partner_types.customer) + '.' }, { size: 's' })),

      el('h4', { text: 'Вид сделка (по данъчен режим)' }),
      note('Кодът от номенклатура 51 за всеки данъчен режим — от него Конто пълни колоните на дневника за продажби. Празно = стойността в сиво.'),
      row(...DEAL_LABELS.map(([key, label]) => field(label, inp('deal_types', key, D.deal_types[key], 20), { size: 's' }))),

      el('h4', { text: 'Валута и дати' }),
      row(hf('Код на валутата (евро)', inp('values', 'currency', D.currency, 20), { what: 'Кодът на еврото в Конто.' }, { size: 's' }),
        hf('Валутен курс', inp('values', 'rate', D.rate, 20, { inputmode: 'decimal' }), { what: 'Курсът, който се пише при евро.' }, { size: 's' }),
        hf('Вальор', sel('values', 'valeur', [['eom', 'Последният ден на месеца (както досега)'], ['doc', 'Датата на документа']], D.valeur || 'eom'), { what: 'С коя дата Конто записва статията.', ex: 'последният ден на месеца.' })),

      el('h4', { text: 'Редът на документа' }),
      note('Ползва се, когато фактурата излиза с един общ ред („Обобщено" в Мои фирми → Склад и Конто), и за редовете без артикул.'),
      row(field('Тип артикул', inp('values', 'article_type', D.article_type, 20), { size: 's' }),
        field('Код на артикул', inp('values', 'article_id', D.article_id, 20), { size: 's' }),
        field('Мярка', inp('values', 'measure', D.measure, 20), { size: 's' })),
      field('Име на реда и обяснителен текст', inp('values', 'article_name', D.article_name, 60))),

    el('details', { class: 'ks-more' },
      el('summary', { text: 'Мерки — код в Конто' }),
      note('За редовете с артикул („По редове"), когато артикулът няма свой код на мярка за Конто.'),
      row(...unitFields)),
    el('details', { class: 'ks-more' },
      el('summary', { text: 'Допълнителни реквизити (по избор)' }),
      note('Ако в Конто ползвате допълнителни реквизити, тук им давате постоянна стойност — пише се на всяка фактура във файла с продажбите.'),
      row(...fieldInputs)),
    el('details', { class: 'ks-more' },
      el('summary', { text: 'Всички колони на файла (' + fmt.COLS.length + ')' }),
      note('Колоните на „Импорт на документи" една по една и откъде идва стойността във всяка.'),
      el('div', { class: 'ks-scroll' }, allCols)));

  // Двата раздела — бутони отгоре (aria-pressed), както табовете в Контакти.
  const tabBtn = (kind, label) => el('button', { type: 'button', 'data-kind': kind, text: label, onClick: () => show(kind) });
  const tabs = el('div', { class: 'seg ks-tabs', role: 'group', 'aria-label': 'Кои настройки' }, tabBtn('sales', 'Продажби (фактури)'), tabBtn('purchases', 'Доставки'), tabBtn('cogs', 'Изписване (склад)'));
  function show(kind) {
    salesBox.hidden = kind !== 'sales';
    purchBox.hidden = kind !== 'purchases';
    cogsBox.hidden = kind !== 'cogs';
    [...tabs.children].forEach((b) => b.setAttribute('aria-pressed', b.dataset.kind === kind ? 'true' : 'false'));
  }
  const body = el('div', { class: 'ks' },
    note('Тук е всичко, което програмата пише във файловете за Конто 6. Попълвате само каквото в Конто на тази фирма е различно. Празно поле = стойността в сиво.'),
    tabs, salesBox, purchBox, cogsBox);
  show(opts && (opts.kind === 'purchases' || opts.kind === 'cogs') ? opts.kind : 'sales');

  async function save() {
    const konto = {};
    for (const sec of Object.keys(inputs)) {
      const vals = {};
      for (const key of Object.keys(inputs[sec])) vals[key] = String(inputs[sec][key].value || '').trim();
      if (sec.startsWith('p:')) { konto.purchases = konto.purchases || {}; konto.purchases[sec.slice(2)] = vals; } else konto[sec] = vals;
    }
    try {
      const saved = await api('/companies/' + company.id + '/konto', { method: 'PUT', body: JSON.stringify({ konto }) });
      // Файлът с продажбите се строи и в браузъра (src/invoices/xlsx.js) от
      // фирмата в паметта — тя трябва да носи новите кодове веднага.
      const list = state().companies || [];
      const i = list.findIndex((c) => String(c.id) === String(company.id));
      if (i >= 0) list[i] = Object.assign({}, list[i], { konto: saved.konto });
      toast('Настройките за Конто са записани.');
      closeModal();
      if (onSaved) onSaved(saved);
    } catch (e) { toast(e.message, true); }
  }

  const foot = el('div', {},
    el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
    el('button', { class: 'btn', text: 'Запази', onClick: save }));
  openModal({ title: 'Настройки за Конто 6 — ' + (company.name || ''), body, foot, wide: true });
  return { inputs, save, show };
}
